import React, { useRef, useEffect, forwardRef, useImperativeHandle, useState, useMemo } from "react";
import { FromSiteWhidOption } from "../hooks/useFromSiteWhid";

interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  onSearch: () => void;
  isDarkTheme?: boolean;
  loading?: boolean;
  includeClosedOrders?: boolean;
  onIncludeClosedOrdersChange?: (checked: boolean) => void;
  warningMessage?: string | null;
  onWarningClose?: () => void;
  fromSiteWhid?: string;
  onFromSiteWhidChange?: (whid: string, organizationId: string) => void;
  fromSiteWhidOptions?: FromSiteWhidOption[];
}

const SearchField = forwardRef<{ focusInput: () => void }, SearchFieldProps>((
  {
    value,
    onChange,
    onSearch,
    isDarkTheme,
    loading = false,
    includeClosedOrders = false,
    onIncludeClosedOrdersChange,
    warningMessage,
    onWarningClose,
    fromSiteWhid = '',
    onFromSiteWhidChange,
    fromSiteWhidOptions = [],
  },
  ref
) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [showWhidDropdown, setShowWhidDropdown] = useState(false);
  const [whidSearch, setWhidSearch] = useState('');
  const [selectedWhidIndex, setSelectedWhidIndex] = useState(-1);
  const whidDropdownRef = useRef<HTMLDivElement>(null);
  const whidInputRef = useRef<HTMLInputElement>(null);

  const filteredWhidOptions = useMemo(() => {
    if (!whidSearch) return fromSiteWhidOptions;
    return fromSiteWhidOptions.filter(opt =>
      opt.whid.toLowerCase().includes(whidSearch.toLowerCase())
    );
  }, [fromSiteWhidOptions, whidSearch]);

  useImperativeHandle(ref, () => ({
    focusInput: () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }));

  // Auto-focus on mount and when cleared
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Close WHID dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        whidDropdownRef.current && !whidDropdownRef.current.contains(event.target as Node) &&
        whidInputRef.current && !whidInputRef.current.contains(event.target as Node)
      ) {
        setShowWhidDropdown(false);
        setWhidSearch('');
        setSelectedWhidIndex(-1);
      }
    };
    if (showWhidDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showWhidDropdown]);

  const handleClear = () => {
    onChange("");
    // Focus after clearing
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSearchDisabled) {
      onSearch();
    }
  };

  const isSearchDisabled = loading || !fromSiteWhid || !value.trim();

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
      {/* From Site WHID Dropdown */}
      <div style={{ position: "relative", display: "inline-block" }} data-tour="from-site-whid">
        <div style={{ position: "relative" }}>
          {(fromSiteWhid) && (
            <label style={{
              position: "absolute",
              top: "-8px",
              left: "8px",
              backgroundColor: isDarkTheme ? "#1a1a1a" : "#fff",
              padding: "0 4px",
              fontSize: "11px",
              color: isDarkTheme ? "#fff" : "#000",
              zIndex: 1
            }}>
              From Site WHID
            </label>
          )}
          <input
            ref={whidInputRef}
            type="text"
            value={showWhidDropdown ? whidSearch : fromSiteWhid}
            onChange={(e) => {
              setWhidSearch(e.target.value);
              if (!showWhidDropdown) setShowWhidDropdown(true);
            }}
            onFocus={() => {
              setWhidSearch(fromSiteWhid);
              setShowWhidDropdown(true);
              setSelectedWhidIndex(-1);
            }}
            onKeyDown={(e) => {
              if (!showWhidDropdown || filteredWhidOptions.length === 0) return;
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedWhidIndex(prev => prev < filteredWhidOptions.length - 1 ? prev + 1 : 0);
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedWhidIndex(prev => prev > 0 ? prev - 1 : filteredWhidOptions.length - 1);
              } else if (e.key === 'Enter' && selectedWhidIndex >= 0) {
                e.preventDefault();
                const selected = filteredWhidOptions[selectedWhidIndex];
                onFromSiteWhidChange?.(selected.whid, selected.organizationId);
                setShowWhidDropdown(false);
                setWhidSearch('');
                setSelectedWhidIndex(-1);
              } else if (e.key === 'Escape') {
                setShowWhidDropdown(false);
                setWhidSearch('');
                setSelectedWhidIndex(-1);
              }
            }}
            placeholder={fromSiteWhid ? "" : "From Site WHID"}
            style={{
              padding: "8px 28px 8px 8px",
              fontSize: "14px",
              fontFamily: "Arial, sans-serif",
              border: `1px solid ${isDarkTheme ? "#555" : "#ccc"}`,
              borderRadius: "4px",
              backgroundColor: isDarkTheme ? "#333" : "#fff",
              color: isDarkTheme ? "#fff" : "#000",
              boxSizing: "border-box",
              width: "120px"
            }}
          />
          {fromSiteWhid ? (
            <span
              onClick={() => {
                onFromSiteWhidChange?.('', '');
                setShowWhidDropdown(false);
                setWhidSearch('');
              }}
              style={{
                position: "absolute",
                right: "6px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
                color: isDarkTheme ? "#ccc" : "#666",
                fontSize: "18px",
                fontWeight: "bold",
                lineHeight: "1"
              }}
            >
              ×
            </span>
          ) : (
            <span
              style={{
                position: "absolute",
                right: "6px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
                display: "flex",
                alignItems: "center",
                color: isDarkTheme ? "#ccc" : "#666"
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </span>
          )}
        </div>
        {showWhidDropdown && filteredWhidOptions.length > 0 && (
          <div
            ref={whidDropdownRef}
            style={{
              position: "absolute",
              top: "100%",
              left: "0",
              backgroundColor: isDarkTheme ? "#333" : "#fff",
              border: `1px solid ${isDarkTheme ? "#555" : "#ccc"}`,
              borderRadius: "4px",
              zIndex: 1000,
              width: "120px",
              maxHeight: "200px",
              overflowY: "auto",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
            }}
          >
            {filteredWhidOptions.map((option, index) => (
              <div
                key={option.whid}
                onClick={() => {
                  onFromSiteWhidChange?.(option.whid, option.organizationId);
                  setShowWhidDropdown(false);
                  setWhidSearch('');
                  setSelectedWhidIndex(-1);
                }}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  backgroundColor: selectedWhidIndex === index ? (isDarkTheme ? '#555' : '#e3f2fd') : 'transparent',
                  color: isDarkTheme ? "#fff" : "#000",
                  borderBottom: `1px solid ${isDarkTheme ? "#555" : "#eee"}`,
                  fontSize: "12px"
                }}
                onMouseEnter={(e) => {
                  if (selectedWhidIndex !== index) e.currentTarget.style.backgroundColor = isDarkTheme ? '#444' : '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  if (selectedWhidIndex !== index) e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                {option.whid}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ position: "relative", display: "inline-block", width: "25%" }} data-tour="search-input">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={!fromSiteWhid}
          placeholder="Search: Request Num, Tracking Num, Serial Num..."
          style={{
            width: "100%",
            padding: "8px",
            paddingRight: "30px",
            fontSize: "14px",
            fontFamily: "Arial, sans-serif",
            border: `1px solid ${isDarkTheme ? "#555" : "#ccc"}`,
            borderRadius: "4px",
            backgroundColor: !fromSiteWhid ? (isDarkTheme ? "#444" : "#f5f5f5") : (isDarkTheme ? "#333" : "#fff"),
            color: !fromSiteWhid ? (isDarkTheme ? "#888" : "#999") : (isDarkTheme ? "#fff" : "#000"),
            boxSizing: "border-box",
            cursor: !fromSiteWhid ? "not-allowed" : "text"
          }}
        />
        {value ? (
          <span
            onClick={handleClear}
            style={{
              position: "absolute",
              right: "8px",
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
              color: isDarkTheme ? "#ccc" : "#666",
              width: "30px",
              height: "30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "normal",
              fontSize: "35px"
            }}
          >
            ×
          </span>
        ) : (
          <span
            style={{
              position: "absolute",
              right: "8px",
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              display: "flex",
              alignItems: "center",
              color: isDarkTheme ? "#ccc" : "#666"
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
          </span>
        )}
      </div>
      <button
        onClick={isSearchDisabled ? undefined : onSearch}
        disabled={isSearchDisabled}
        data-tour="search-button"
        style={{
          padding: "8px 16px",
          fontSize: "14px",
          fontFamily: "Arial, sans-serif",
          border: "none",
          borderRadius: "4px",
          backgroundColor: isSearchDisabled ? "#6c757d" : "#007bff",
          color: "white",
          cursor: isSearchDisabled ? "not-allowed" : "pointer",
          transition: "background-color 0.3s",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          opacity: isSearchDisabled ? 0.7 : 1
        }}
        onMouseOver={(e) => !isSearchDisabled && (e.currentTarget.style.backgroundColor = "#0056b3")}
        onMouseOut={(e) => !isSearchDisabled && (e.currentTarget.style.backgroundColor = "#007bff")}
      >
        {loading && (
          <div
            style={{
              width: "16px",
              height: "16px",
              border: "2px solid #ffffff",
              borderTop: "2px solid transparent",
              borderRadius: "50%",
              animation: "spin 1s linear infinite"
            }}
          />
        )}
        {loading ? "Searching..." : "Search"}
      </button>
      <label style={{
        display: "flex",
        alignItems: "center",
        gap: "5px",
        fontSize: "14px",
        color: isDarkTheme ? "#fff" : "#000",
        cursor: "pointer"
      }} data-tour="include-closed">
        <input
          type="checkbox"
          checked={includeClosedOrders}
          onChange={(e) => onIncludeClosedOrdersChange?.(e.target.checked)}
          style={{
            cursor: "pointer",
            width: "20px",
            height: "20px"
          }}
        />
        Include Received Orders
      </label>
      {warningMessage && (
        <div style={{
          backgroundColor: isDarkTheme ? '#8b0000' : '#f8d7da',
          color: isDarkTheme ? '#fff' : '#721c24',
          padding: '6px 12px',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          fontSize: '13px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {warningMessage}
          <span
            onClick={onWarningClose}
            style={{
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
              color: isDarkTheme ? '#fff' : '#721c24'
            }}
          >
            ×
          </span>
        </div>
      )}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
});

SearchField.displayName = 'SearchField';

export default SearchField;
